/**
 * @file useModal.js
 * @description Hooks para gerenciamento de estado de modais com ou sem dados associados
 * 
 * @exports useModalState - Hook simples para controlar abertura/fechamento
 * @exports useModalWithData - Hook que adiciona gerenciamento de dados ao modal
 */

import { useState, useCallback } from 'react';

/**
 * Hook para gerenciar estado de modal (open/close)
 * 
 * @param {boolean} initialState - Estado inicial do modal (padrão: false)
 * @returns {Object} { isOpen, open, close, toggle }
 * 
 * @example
 * const modal = useModalState();
 * <button onClick={modal.open}>Abrir</button>
 * <Modal isOpen={modal.isOpen} onClose={modal.close}>...</Modal>
 */
export const useModalState = (initialState = false) => {
  const [isOpen, setIsOpen] = useState(initialState);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  return { isOpen, open, close, toggle };
};

/**
 * Hook para gerenciar modal com dados associados (ex: modal de edição)
 * 
 * @returns {Object} { isOpen, data, open, close, updateData }
 * 
 * @example
 * const editModal = useModalWithData();
 * <button onClick={() => editModal.open(item)}>Editar</button>
 * <EditModal 
 *   isOpen={editModal.isOpen} 
 *   data={editModal.data}
 *   onClose={editModal.close}
 * />
 */
export const useModalWithData = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState(null);

  const open = useCallback((initialData = null) => {
    setData(initialData);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setData(null);
  }, []);

  const updateData = useCallback((newData) => setData(newData), []);

  return { isOpen, data, open, close, updateData };
};
